<template>
  <head>
    <meta charset="utf-8" />
    <title>CBHI | Disease Surveillance</title>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta content="Report and track disease cases in the community" name="description" />
    <meta content="CBHI System" name="author" />
    <!-- App favicon -->
    <link rel="shortcut icon" href="assets/images/favicon.svg">

    <!-- Bootstrap Css -->
    <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
    <!-- Icons Css -->
    <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
    <!-- App Css-->
    <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
    <!-- custom Css-->
    <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
  </head>

  <body data-sidebar="dark">
  <!-- Begin page -->
  <div id="layout-wrapper">
    <!-- ========== Left Sidebar Start ========== -->
    <NavigationBar/>
    <!-- Left Sidebar End -->

    <!-- ============================================================== -->
    <!-- Start right Content here -->
    <!-- ============================================================== -->
    <div class="main-content">
      <div class="page-content">
        <div class="container-fluid">
          <!-- start page title -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Disease Surveillance</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="javascript: void(0);">HEW</a></li>
                    <li class="breadcrumb-item active">Disease Surveillance</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Alerts -->
          <div v-if="alert.success" class="alert alert-success" role="alert">
            {{message}}
          </div>
          <div v-if="alert.info" class="alert alert-primary" role="alert">
            {{message}}
          </div>
          <div v-if="alert.failed" class="alert alert-danger" role="alert">
            {{message}}
          </div>

          <!-- Alert Banner for Notifiable Diseases -->
          <div v-if="hasNotifiableCases" class="alert alert-danger alert-dismissible fade show" role="alert">
            <h5 class="alert-heading">
              <i class="bx bx-error-circle me-2"></i>Immediate Action Required!
            </h5>
            <p class="mb-0">
              You have {{ notifiableCasesCount }} notifiable disease case(s) that must be reported to Woreda Health Office immediately.
            </p>
            <hr>
            <div class="d-flex justify-content-end">
              <button @click="showNotifiableOnly" class="btn btn-danger btn-sm">
                View Notifiable Cases
              </button>
            </div>
          </div>

          <!-- Statistics Cards -->
          <div class="row">
            <div class="col-lg-3 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex flex-wrap">
                    <div class="me-3">
                      <p class="text-muted mb-2">Total Cases (This Month)</p>
                      <h5 class="mb-0">{{ statistics.total_cases || 0 }}</h5>
                    </div>
                    <div class="avatar-sm ms-auto">
                      <div class="avatar-title bg-primary rounded-circle text-white font-size-20">
                        <i class="bx bx-plus-medical"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-3 col-md-6">
              <div class="card blog-stats-wid">
                <div class="card-body">
                  <div class="d-flex flex-wrap">
                    <div class="me-3">
                      <p class="text-muted mb-2">Active Outbreaks</p>
                      <h5 class="mb-0">{{ statistics.active_outbreaks || 0 }}</h5>
                    </div>
                    <div class="avatar-sm ms-auto">
                      <div class="avatar-title bg-danger rounded-circle text-white font-size-20">
                        <i class="bx bx-error"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-3 col-md-6">
              <div class="card blog-stats-wid">
                <div class="card-body">
                  <div class="d-flex flex-wrap">
                    <div class="me-3">
                      <p class="text-muted mb-2">Households Affected</p>
                      <h5 class="mb-0">{{ statistics.affected_households || 0 }}</h5>
                    </div>
                    <div class="avatar-sm ms-auto">
                      <div class="avatar-title bg-warning rounded-circle text-white font-size-20">
                        <i class="bx bx-home-circle"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-3 col-md-6">
              <div class="card blog-stats-wid">
                <div class="card-body">
                  <div class="d-flex flex-wrap">
                    <div class="me-3">
                      <p class="text-muted mb-2">Reported to Woreda</p>
                      <h5 class="mb-0">{{ statistics.reported_to_woreda || 0 }}</h5>
                    </div>
                    <div class="avatar-sm ms-auto">
                      <div class="avatar-title bg-success rounded-circle text-white font-size-20">
                        <i class="bx bx-check-shield"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Disease Summary Chart -->
          <div class="row">
            <div class="col-lg-8">
              <div class="card">
                <div class="card-header bg-white">
                  <h5 class="mb-0">Disease Distribution</h5>
                </div>
                <div class="card-body">
                  <div v-if="diseaseSummary.length === 0" class="text-center py-4">
                    <i class="bx bx-bar-chart-alt-2 text-muted" style="font-size: 48px;"></i>
                    <p class="mt-3 text-muted">No disease cases reported this period</p>
                  </div>
                  <div v-else>
                    <div v-for="disease in diseaseSummary" :key="disease.disease_type" class="mb-3">
                      <div class="d-flex justify-content-between align-items-center mb-1">
                        <h6 class="mb-0">
                          {{ disease.disease_type }}
                          <span v-if="isNotifiable(disease.disease_type)"
                                class="badge bg-danger ms-2">Notifiable</span>
                        </h6>
                        <span class="text-muted">{{ disease.case_count }} cases</span>
                      </div>
                      <div class="progress" style="height: 10px;">
                        <div class="progress-bar"
                             :class="getProgressBarClass(disease.disease_type)"
                             role="progressbar"
                             :style="{width: getPercentage(disease.case_count) + '%'}"
                             :aria-valuenow="disease.case_count"
                             aria-valuemin="0"
                             :aria-valuemax="maxCases">
                        </div>
                      </div>
                      <small class="text-muted">
                        {{ disease.affected_households }} households •
                        {{ disease.severe_cases }} severe
                        <span v-if="disease.reported_to_woreda > 0" class="text-success">
                          • {{ disease.reported_to_woreda }} reported
                        </span>
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Quick Actions -->
            <div class="col-lg-4">
              <div class="card">
                <div class="card-header bg-white">
                  <h5 class="mb-0">Quick Actions</h5>
                </div>
                <div class="card-body">
                  <div class="d-grid gap-2">
                    <button @click="showReportModal = true" class="btn btn-danger">
                      <i class="bx bx-plus-circle me-2"></i>Report New Case
                    </button>
                    <button @click="showOutbreakModal = true" class="btn btn-warning">
                      <i class="bx bx-error-circle me-2"></i>Declare Outbreak
                    </button>
                    <button @click="downloadReport" class="btn btn-outline-primary">
                      <i class="bx bx-download me-2"></i>Download Report
                    </button>
                    <button @click="showGuidelines" class="btn btn-outline-secondary">
                      <i class="bx bx-book-open me-2"></i>Disease Guidelines
                    </button>
                  </div>

                  <!-- Recent Alerts -->
                  <div class="mt-4">
                    <h6 class="mb-3">Recent Alerts</h6>
                    <div v-if="recentAlerts.length === 0" class="text-center text-muted">
                      <small>No recent alerts</small>
                    </div>
                    <div v-else>
                      <div v-for="alert in recentAlerts" :key="alert.id"
                           class="alert alert-danger py-2 px-3 mb-2">
                        <small>
                          <i class="bx bx-error-circle me-1"></i>
                          {{ alert.message }}
                          <br>
                          <span class="text-muted">{{ formatTimeAgo(alert.created_at) }}</span>
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Disease Cases Table -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-white d-flex justify-content-between align-items-center w-100 border-bottom-1 pb-3 border-bottom">
                  <!-- Filters -->
                  <div class="me-3 text-nowrap filter-tool-bar-btns">
                    <!-- Disease Type Filter -->
                    <div class="btn-group">
                      <button type="button" class="btn btn-light waves-effect" data-bs-toggle="dropdown">
                        <i class="bx bx-filter font-size-16 me-1 align-middle"></i>
                        {{ filters.diseaseType || 'All Diseases' }}
                      </button>
                      <div class="dropdown-menu" style="max-height: 300px; overflow-y: auto;">
                        <a class="dropdown-item" @click="filterByDisease('')">All Diseases</a>
                        <div class="dropdown-divider"></div>
                        <h6 class="dropdown-header text-danger">Notifiable Diseases</h6>
                        <a v-for="disease in notifiableDiseases" :key="disease"
                           class="dropdown-item" @click="filterByDisease(disease)">
                          {{ disease }}
                        </a>
                        <div class="dropdown-divider"></div>
                        <h6 class="dropdown-header">Other Diseases</h6>
                        <a v-for="disease in otherDiseases" :key="disease"
                           class="dropdown-item" @click="filterByDisease(disease)">
                          {{ disease }}
                        </a>
                      </div>
                    </div>

                    <!-- Date Range Filter -->
                    <div class="btn-group ms-2">
                      <button type="button" class="btn btn-light waves-effect" data-bs-toggle="dropdown">
                        <i class="bx bx-calendar font-size-16 me-1 align-middle"></i>
                        {{ getDateRangeLabel() }}
                      </button>
                      <div class="dropdown-menu p-4" style="width: 300px;">
                        <div class="row">
                          <div class="col-12 mb-2">
                            <label for="start-date-input" class="form-label">Start Date</label>
                            <input v-model="filters.startDate" type="date" class="form-control" id="start-date-input">
                          </div>
                          <div class="col-12 mb-2">
                            <label for="end-date-input" class="form-label">End Date</label>
                            <input v-model="filters.endDate" type="date" class="form-control" id="end-date-input">
                          </div>
                          <div class="col-12 mt-2">
                            <button @click="applyDateFilter" type="button" class="btn btn-primary w-100">Apply Filter</button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Clear Filters -->
                    <button v-if="hasActiveFilters" type="button" class="btn btn-danger waves-effect ms-2"
                            @click="clearFilters">
                      <i class="bx bx-x font-size-16 me-1 align-middle"></i> Clear
                    </button>
                  </div>

                  <div class="btn-toolbar d-flex justify-content-between align-items-center w-100" role="toolbar">
                    <div class="flex-grow-1">
                      <div class="search-box mb-0 me-3">
                        <div class="position-relative">
                          <input v-model="searchQuery" @keyup.enter="searchCases" type="text"
                                 class="form-control bg-light border-light rounded"
                                 placeholder="Search by member name, household, symptoms...">
                          <i class="bx bx-search-alt search-icon"></i>
                        </div>
                      </div>
                    </div>

                    <div class="d-flex gap-2">
                      <!-- Export -->
                      <button @click="exportData" type="button" class="btn btn-outline-primary">
                        <i class="bx bx-export"></i> <span class="d-md-inline ms-1">Export</span>
                      </button>

                      <!-- Report New Case -->
                      <button @click="showReportModal = true" type="button" class="btn btn-danger">
                        <i class="bx bx-plus"></i> <span class="d-md-inline ms-1">Report Case</span>
                      </button>
                    </div>
                  </div>
                </div>

                <div class="card-body">
                  <!-- Loading State -->
                  <div v-if="loading" class="text-center py-5">
                    <div class="spinner-border text-primary" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>
                    <p class="mt-3">Loading disease cases...</p>
                  </div>

                  <!-- No Data State -->
                  <div v-else-if="!loading && cases.length === 0" class="text-center py-5">
                    <i class="bx bx-plus-medical text-muted" style="font-size: 48px;"></i>
                    <p class="mt-3 text-muted">No disease cases found</p>
                  </div>

                  <!-- Data Table -->
                  <div v-else class="table-responsive">
                    <table class="table align-middle table-hover border table-striped text-nowrap mb-0">
                      <thead class="table-light">
                      <tr>
                        <th scope="col">Case ID</th>
                        <th scope="col">Patient</th>
                        <th scope="col">Disease</th>
                        <th scope="col">Onset Date</th>
                        <th scope="col">Symptoms</th>
                        <th scope="col">Severity</th>
                        <th scope="col">Status</th>
                        <th scope="col" class="text-center">Actions</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="case_ in cases" :key="case_.surveillance_id"
                          :class="{'table-danger': isNotifiable(case_.disease_type) && !case_.reported_to_woreda}">
                        <td>
                          <div>
                            <h6 class="mb-0">#{{ case_.surveillance_id }}</h6>
                            <small class="text-muted">{{ formatDate(case_.reported_date) }}</small>
                          </div>
                        </td>
                        <td>
                          <div class="d-flex align-items-center">
                            <div class="avatar-xs me-2">
                              <div class="avatar-title bg-primary text-white rounded-circle">
                                {{ getInitials(case_.first_name, case_.surname) }}
                              </div>
                            </div>
                            <div>
                              <h6 class="mb-0">{{ case_.first_name }} {{ case_.surname }}</h6>
                              <small class="text-muted">{{ case_.household_number }}</small>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div>
                            <h6 class="mb-0">{{ case_.disease_type }}</h6>
                            <span v-if="isNotifiable(case_.disease_type)"
                                  class="badge bg-danger">Notifiable</span>
                          </div>
                        </td>
                        <td>
                          <div>
                            {{ formatDate(case_.onset_date) }}
                            <div class="text-muted small">
                              {{ getDaysAgo(case_.onset_date) }} days ago
                            </div>
                          </div>
                        </td>
                        <td>
                          <div class="text-truncate" style="max-width: 200px;"
                               :title="case_.symptoms">
                            {{ case_.symptoms || 'Not specified' }}
                          </div>
                        </td>
                        <td>
                          <span :class="getSeverityBadge(case_.severity)">
                            {{ case_.severity }}
                          </span>
                        </td>
                        <td>
                          <div>
                            <span v-if="case_.reported_to_woreda" class="badge bg-success">
                              <i class="bx bx-check"></i> Reported
                            </span>
                            <span v-else-if="isNotifiable(case_.disease_type)" class="badge bg-danger">
                              <i class="bx bx-x"></i> Not Reported
                            </span>
                            <span v-else class="badge bg-secondary">
                              Monitoring
                            </span>
                            <div v-if="case_.isolation_required" class="mt-1">
                              <span class="badge bg-warning">
                                <i class="bx bx-shield"></i> Isolation
                              </span>
                            </div>
                          </div>
                        </td>
                        <td class="text-center">
                          <div class="btn-group" role="group">
                            <button @click="viewCase(case_)" class="btn btn-sm btn-primary"
                                    title="View Details">
                              <i class="bx bx-show"></i>
                            </button>
                            <button v-if="!case_.reported_to_woreda && isNotifiable(case_.disease_type)"
                                    @click="reportToWoreda(case_)"
                                    class="btn btn-sm btn-danger" title="Report to Woreda">
                              <i class="bx bx-send"></i>
                            </button>
                            <button @click="updateCase(case_)" class="btn btn-sm btn-warning"
                                    title="Update">
                              <i class="bx bx-edit"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- Pagination -->
                  <div v-if="totalPages > 1" class="row mt-4">
                    <div class="col-sm-12 col-md-6">
                      <div class="dataTables_info">
                        Showing {{ (currentPage - 1) * limit + 1 }} to {{ Math.min(currentPage * limit, total) }} of {{ total }} entries
                      </div>
                    </div>
                    <div class="col-sm-12 col-md-6">
                      <div class="dataTables_paginate paging_simple_numbers float-end">
                        <ul class="pagination">
                          <li class="paginate_button page-item previous" :class="{'disabled': currentPage === 1}">
                            <a @click="changePage(currentPage - 1)" href="#" class="page-link">Previous</a>
                          </li>
                          <li v-for="page in displayedPages" :key="page" class="paginate_button page-item"
                              :class="{'active': page === currentPage}">
                            <a @click="changePage(page)" href="#" class="page-link">{{ page }}</a>
                          </li>
                          <li class="paginate_button page-item next" :class="{'disabled': currentPage === totalPages}">
                            <a @click="changePage(currentPage + 1)" href="#" class="page-link">Next</a>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- container-fluid -->
      </div>
      <!-- End Page-content -->

      <!-- Import Modal Components -->
      <ReportDiseaseModal
          v-if="showReportModal"
          @close="showReportModal = false"
          @reported="onCaseReported"
      />

      <DeclareOutbreakModal
          v-if="showOutbreakModal"
          :diseaseSummary="diseaseSummary"
          @close="showOutbreakModal = false"
          @declared="onOutbreakDeclared"
      />

      <CaseDetailsModal
          v-if="showDetailsModal"
          :case="selectedCase"
          @close="showDetailsModal = false"
          @updated="onCaseUpdated"
      />

      <UpdateCaseModal
          v-if="showUpdateModal"
          :case="selectedCase"
          @close="showUpdateModal = false"
          @updated="onCaseUpdated"
      />

      <footer class="footer">
        <div class="container-fluid">
          <div class="row">
            <div class="col-sm-6">© CBHI</div>
            <div class="col-sm-6">
              <div class="text-sm-end d-sm-block">
                Ministry of Health - Ethiopia
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
    <!-- end main content-->
  </div>
  <!-- END layout-wrapper -->

  <!-- Right bar overlay-->
  <div class="rightbar-overlay"></div>
  </body>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import ReportDiseaseModal from "@/components/hew/modals/ReportDiseaseModal.vue";
import DeclareOutbreakModal from "@/components/hew/modals/DeclareOutbreakModal.vue";
import CaseDetailsModal from "@/components/hew/modals/CaseDetailsModal.vue";
import UpdateCaseModal from "@/components/hew/modals/UpdateCaseModal.vue";
import { authUrl, executeJson } from "@/api";
import router from "@/router";

export default {
  name: "DiseaseSurveillance",
  components: {
    NavigationBar,
    ReportDiseaseModal,
    DeclareOutbreakModal,
    CaseDetailsModal,
    UpdateCaseModal
  },
  data() {
    return {
      // Loading states
      loading: false,

      // Data
      cases: [],
      diseaseSummary: [],
      selectedCase: null,
      statistics: {
        total_cases: 0,
        active_outbreaks: 0,
        affected_households: 0,
        reported_to_woreda: 0
      },
      recentAlerts: [],

      // Filters
      filters: {
        diseaseType: '',
        startDate: this.getDefaultStartDate(),
        endDate: new Date().toISOString().split('T')[0]
      },

      // Search
      searchQuery: '',

      // Pagination
      currentPage: 1,
      limit: 20,
      total: 0,

      // UI States
      showReportModal: false,
      showOutbreakModal: false,
      showDetailsModal: false,
      showUpdateModal: false,

      // Disease lists
      notifiableDiseases: ['AFP', 'Measles', 'Cholera', 'Meningitis', 'Yellow Fever', 'COVID-19', 'Polio', 'Rabies'],
      otherDiseases: ['Malaria', 'Diarrhea', 'Pneumonia', 'Typhoid', 'TB', 'Skin Infection', 'Eye Infection'],

      // Alert states
      alert: {
        success: false,
        info: false,
        failed: false
      },
      message: ''
    };
  },

  computed: {
    totalPages() {
      return Math.ceil(this.total / this.limit);
    },

    displayedPages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);

      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    },

    hasActiveFilters() {
      return this.filters.diseaseType !== '' ||
          this.filters.startDate !== this.getDefaultStartDate();
    },

    hasNotifiableCases() {
      return this.cases.some(c => this.isNotifiable(c.disease_type) && !c.reported_to_woreda);
    },

    notifiableCasesCount() {
      return this.cases.filter(c => this.isNotifiable(c.disease_type) && !c.reported_to_woreda).length;
    },

    maxCases() {
      return Math.max(...this.diseaseSummary.map(d => d.case_count), 1);
    }
  },

  mounted() {

    this.loadDiseaseCases();
    this.loadSummary();
    this.loadAlerts();
  },

  methods: {
    checkAuth() {
      const token = localStorage.getItem("authToken");
      const userRole = sessionStorage.getItem("userRoleId");

      if (!token) {
        router.push('/login');
        return;
      }

      // Check if user is HEW (role_id = 38)
      if (userRole !== '38') {
        this.showAlert('failed', 'Access denied. HEW role required.');
        router.push('/dashboard');
      }
    },

    async loadDiseaseCases() {
      this.loading = true;

      const data = {
        "function": "getDiseaseSurveillance",
        "startDate": this.filters.startDate,
        "endDate": this.filters.endDate,
        "diseaseType": this.filters.diseaseType
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.cases = res.data.data.cases || [];
          this.total = this.cases.length;
        } else {
          this.showAlert('failed', res.data.message || 'Failed to load disease cases');
        }
      } catch (e) {
        console.error("Failed to load disease cases:", e);
        this.showAlert('failed', 'Failed to load disease cases');
      } finally {
        this.loading = false;
      }
    },

    async loadSummary() {
      const data = {
        "function": "getDiseaseSurveillance",
        "startDate": this.filters.startDate,
        "endDate": this.filters.endDate
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.diseaseSummary = res.data.data.summary || [];
          this.calculateStatistics();
        }
      } catch (e) {
        console.error("Failed to load summary:", e);
      }
    },

    loadAlerts() {
      // Simulate recent alerts - would come from API
      this.recentAlerts = [
        {
          id: 1,
          message: "3 cases of Measles reported in Gote 02",
          created_at: new Date(Date.now() - 2 * 60 * 60 * 1000)
        },
        {
          id: 2,
          message: "Cholera outbreak declared in neighboring kebele",
          created_at: new Date(Date.now() - 24 * 60 * 60 * 1000)
        }
      ];
    },

    calculateStatistics() {
      this.statistics = {
        total_cases: this.diseaseSummary.reduce((sum, d) => sum + d.case_count, 0),
        active_outbreaks: this.diseaseSummary.filter(d => d.case_count >= 5).length,
        affected_households: this.diseaseSummary.reduce((sum, d) => sum + d.affected_households, 0),
        reported_to_woreda: this.diseaseSummary.reduce((sum, d) => sum + d.reported_to_woreda, 0)
      };
    },

    filterByDisease(diseaseType) {
      this.filters.diseaseType = diseaseType;
      this.currentPage = 1;
      this.loadDiseaseCases();
    },

    applyDateFilter() {
      this.currentPage = 1;
      this.loadDiseaseCases();
      this.loadSummary();
    },

    async clearFilters() {
      this.filters = {
        diseaseType: '',
        startDate: this.getDefaultStartDate(),
        endDate: new Date().toISOString().split('T')[0]
      };
      this.searchQuery = '';
      this.currentPage = 1;
      await this.loadDiseaseCases();
      await this.loadSummary();
    },

    async searchCases() {
      this.currentPage = 1;
      await this.loadDiseaseCases();
    },

    async changePage(page) {
      if (page < 1 || page > this.totalPages) return;

      this.currentPage = page;
      await this.loadDiseaseCases();
    },

    showNotifiableOnly() {
      this.filters.diseaseType = '';
      this.cases = this.cases.filter(c => this.isNotifiable(c.disease_type) && !c.reported_to_woreda);
    },

    viewCase(case_) {
      this.selectedCase = case_;
      this.showDetailsModal = true;
    },

    updateCase(case_) {
      this.selectedCase = case_;
      this.showUpdateModal = true;
    },

    async reportToWoreda(case_) {
      if (!confirm(`Report ${case_.disease_type} case to Woreda Health Office?`)) return;

      const data = {
        "function": "reportDiseaseToWoreda",
        "surveillanceId": case_.surveillance_id
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.showAlert('success', 'Case reported to Woreda successfully');
          await this.loadDiseaseCases();
        } else {
          this.showAlert('failed', res.data.message || 'Failed to report case');
        }
      } catch (e) {
        console.error("Failed to report case:", e);
        this.showAlert('failed', 'Failed to report case to Woreda');
      }
    },

    async downloadReport() {
      // Implementation for downloading report
      this.showAlert('info', 'Generating report...');
    },

    showGuidelines() {
      // Navigate to guidelines or show modal
      this.showAlert('info', 'Opening disease management guidelines...');
    },

    async exportData() {
      // Implementation for exporting data
      this.showAlert('info', 'Exporting data...');
    },

    // Modal callbacks
    onCaseReported() {
      this.showReportModal = false;
      this.showAlert('success', 'Disease case reported successfully');
      this.loadDiseaseCases();
      this.loadSummary();
    },

    onOutbreakDeclared() {
      this.showOutbreakModal = false;
      this.showAlert('success', 'Outbreak declared and reported to Woreda');
      this.loadDiseaseCases();
      this.loadSummary();
    },

    onCaseUpdated() {
      this.showDetailsModal = false;
      this.showUpdateModal = false;
      this.showAlert('success', 'Case updated successfully');
      this.loadDiseaseCases();
    },

    // Utility methods
    getDefaultStartDate() {
      const date = new Date();
      date.setMonth(date.getMonth() - 1);
      return date.toISOString().split('T')[0];
    },

    getDateRangeLabel() {
      const start = new Date(this.filters.startDate);
      const end = new Date(this.filters.endDate);

      if (this.filters.startDate === this.getDefaultStartDate() &&
          this.filters.endDate === new Date().toISOString().split('T')[0]) {
        return 'Last 30 days';
      }

      return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
    },

    isNotifiable(diseaseType) {
      return this.notifiableDiseases.includes(diseaseType);
    },

    getPercentage(count) {
      return Math.round((count / this.maxCases) * 100);
    },

    getProgressBarClass(diseaseType) {
      if (this.isNotifiable(diseaseType)) return 'bg-danger';
      return 'bg-primary';
    },

    getInitials(firstName, lastName) {
      return ((firstName || '')[0] || '') + ((lastName || '')[0] || '');
    },

    getSeverityBadge(severity) {
      const badges = {
        'mild': 'badge bg-success',
        'moderate': 'badge bg-warning',
        'severe': 'badge bg-danger'
      };
      return badges[severity] || 'badge bg-secondary';
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    getDaysAgo(dateString) {
      if (!dateString) return 0;
      const date = new Date(dateString);
      const today = new Date();
      const diffTime = Math.abs(today - date);
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    },

    formatTimeAgo(date) {
      const now = new Date();
      const diff = now - date;
      const hours = Math.floor(diff / (1000 * 60 * 60));

      if (hours < 1) return 'Just now';
      if (hours < 24) return `${hours} hours ago`;

      const days = Math.floor(hours / 24);
      return `${days} days ago`;
    },

    // Alert methods
    clearAlerts() {
      this.alert.success = false;
      this.alert.info = false;
      this.alert.failed = false;
    },

    showAlert(type, message) {
      this.clearAlerts();
      this.alert[type] = true;
      this.message = message;

      if (type === 'success' || type === 'info') {
        setTimeout(() => {
          this.clearAlerts();
        }, 5000);
      }
    }
  }
};
</script>

<style scoped>
.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-weight: 600;
}

.search-icon {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  color: #999;
}

.table-danger {
  background-color: rgba(220, 53, 69, 0.05);
}

.progress {
  background-color: #e9ecef;
}

.filter-tool-bar-btns .btn-group {
  margin-right: 5px;
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>